import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Spin, message, Button, Space } from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    CalendarOutlined,
    DollarOutlined,
    EyeOutlined,
    MedicineBoxOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services';
import { useApp } from '../../context/AppContext';
import CurrencyDisplay from '../../components/CurrencyDisplay/CurrencyDisplay';
import SubscriptionInfo from '../../components/SubscriptionInfo';
import dayjs from 'dayjs';

const Dashboard = () => {
    const { t } = useTranslation(['dashboard', 'common']);
    const { user } = useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await dashboardService.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            message.error(t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip={t('common:loading')} />
            </div>
        );
    }

    const statusColors = {
        Scheduled: 'blue',
        Confirmed: 'green',
        'In Progress': 'orange',
        Completed: 'success',
        Cancelled: 'error',
        'No Show': 'default'
    };

    const appointmentColumns = [
        {
            title: t('patient:name'),
            dataIndex: ['patientId', 'name'],
            key: 'patient'
        },
        {
            title: t('dentist:name'),
            dataIndex: ['dentistId', 'name'],
            key: 'dentist'
        },
        {
            title: t('appointment:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: t('appointment:time'),
            dataIndex: 'time',
            key: 'time'
        },
        {
            title: t('appointment:status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={statusColors[status]}>{status}</Tag>
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/patients/${record.patientId?._id}`)}
                        title={t('common:viewPatientDetails')}
                    >
                        {t('common:view')}
                    </Button>
                    <Button
                        type="default"
                        size="small"
                        icon={<MedicineBoxOutlined />}
                        onClick={() => navigate(`/treatments?patientId=${record.patientId?._id}`)}
                        title={t('common:createTreatment')}
                    >
                        {t('common:treatment')}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>{t('dashboard:title')}</h1>
            {user?.role === 'admin' && <SubscriptionInfo />}

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:totalPatients')}
                            value={stats?.overview?.totalPatients || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:totalDentists')}
                            value={stats?.overview?.totalDentists || 0}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:todayAppointments')}
                            value={stats?.overview?.todayAppointments || 0}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <div>
                            <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 8 }}>
                                {t('dashboard:totalRevenue')}
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#faad14' }}>
                                <DollarOutlined style={{ marginRight: 8 }} />
                                <CurrencyDisplay
                                    amount={stats?.overview?.totalRevenue || 0}
                                    currency="USD"
                                    showBoth={true}
                                    size="large"
                                />
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* More Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:upcomingAppointments')}
                            value={stats?.overview?.upcomingAppointments || 0}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:totalAppointments')}
                            value={stats?.overview?.totalAppointments || 0}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:pendingPayments')}
                            value={stats?.overview?.pendingPaymentsCount || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('dashboard:pendingAmount')}
                            value={stats?.overview?.pendingPaymentsAmount || 0}
                            prefix="$"
                            precision={2}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Recent Appointments */}
            <Card title={user?.role === 'dentist' ? t('dashboard:todayAppointments', "Today's Appointments") : t('dashboard:recentAppointments')} style={{ marginBottom: 24 }}>
                <Table
                    dataSource={stats?.recentAppointments || []}
                    columns={appointmentColumns}
                    rowKey="_id"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default Dashboard;
